import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { StudioArt } from '../../types'
import { GlassPanel, monoLabelSx } from '../shared'

export interface StudioTourBarProps {
  arts: StudioArt[]
  activeIndex: number
  isPlaying: boolean
  onPlayToggle: () => void
  onJumpTo: (index: number) => void
  /**
   * `guest` — full-bleed /g viewer (no side panels). `tour` — Studio's own
   * Design/Tour tab, side panels hidden while touring (task #14) so the
   * strip can use the same widened, no-side-panel layout as guest, minus
   * guest's mobile walk-pad clearance (nothing to clear inside the editor).
   * `studio` — default, side panels visible, narrower to stay clear of them.
   */
  variant?: 'studio' | 'guest' | 'tour'
  /** CSS bottom offset — guest uses this to clear the present HUD / signup banner. */
  bottom?: number
}

const Root = styled(GlassPanel, {
  shouldForwardProp: (p) => p !== '$wide' && p !== '$guest' && p !== '$bottom',
})<{ $wide?: boolean; $guest?: boolean; $bottom: number }>(({ $wide, $guest, $bottom }) => ({
  position: 'absolute',
  left: '50%',
  bottom: $bottom,
  transform: 'translateX(-50%)',
  zIndex: 39,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '9px 11px',
  borderRadius: 15,
  // No forced `width` here — this shrinks to fit however many stops there
  // are (a 3-stop tour and a 30-stop tour shouldn't render the same bar
  // width) and only grows up to `maxWidth`, at which point `Stops`'
  // internal `overflowX: auto` takes over instead of the bar itself
  // stretching into empty space past the last stop.
  maxWidth: $wide ? 'min(860px, calc(100vw - 32px))' : 'min(860px, calc(100vw - 560px))',
  // Guest mobile: walk pad + present HUD sit ~110px up — keep the story
  // strip above them so stops aren't covered. Tour has neither, so it never
  // needs this even though it shares guest's widened layout.
  ...($guest
    ? {
        '@media (max-width: 720px), (hover: none), (pointer: coarse)': {
          bottom: Math.max($bottom, 152),
        },
      }
    : null),
}))

const PlayBtn = styled('button')({
  all: 'unset',
  width: 34,
  height: 34,
  borderRadius: 10,
  background: 'var(--ds-gradient-spectrum)',
  color: '#17130a',
  fontSize: 13,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
})

// Scrollable, but the mockup hides the scrollbar itself (still wheel/drag
// scrollable) — without this it shows the browser's default scrollbar strip.
const Stops = styled(Box)({
  display: 'flex',
  gap: 6,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

const Stop = styled('button', { shouldForwardProp: (p) => p !== '$now' })<{ $now?: boolean }>(({ $now }) => ({
  all: 'unset',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  height: 40,
  padding: '0 10px 0 5px',
  borderRadius: 10,
  border: `1px solid ${$now ? 'rgba(190,157,253,0.55)' : 'var(--ds-night-border)'}`,
  background: $now ? 'rgba(190,157,253,0.12)' : 'rgba(244,243,246,0.04)',
  cursor: 'pointer',
  transition: 'all 140ms ease',
  '&:hover': { background: 'rgba(244,243,246,0.09)' },
}))

const Thumb = styled('img')({ width: 40, height: 30, borderRadius: 6, objectFit: 'cover' })
const StopNum = styled(Box)({ ...monoLabelSx, fontSize: 7 })
const StopTitle = styled(Box)({
  fontSize: 10.5,
  fontWeight: 620,
  whiteSpace: 'nowrap',
  maxWidth: 90,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'var(--ds-night-ink)',
})

/** Guided-tour playback bar — Studio Tour tab, or guest `/g` STORY strip. */
export function StudioTourBar({
  arts,
  activeIndex,
  isPlaying,
  onPlayToggle,
  onJumpTo,
  variant = 'studio',
  bottom,
}: StudioTourBarProps) {
  const guest = variant === 'guest'
  const wide = variant !== 'studio'
  return (
    <Root $wide={wide} $guest={guest} $bottom={bottom ?? (guest ? 88 : 72)}>
      <PlayBtn onClick={onPlayToggle} aria-label={isPlaying ? 'Pause story' : 'Play story'}>
        {isPlaying ? '⏸' : '▶'}
      </PlayBtn>
      <Stops>
        {arts.map((art, i) => (
          <Stop key={art.id} $now={i === activeIndex} onClick={() => onJumpTo(i)}>
            <Thumb src={art.img} alt='' />
            <Box>
              <StopNum>STOP {String(i + 1).padStart(2, '0')}</StopNum>
              <StopTitle>{art.name}</StopTitle>
            </Box>
          </Stop>
        ))}
      </Stops>
    </Root>
  )
}
